import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { directLogout } from "../../utils/tokenManager";
import menu from "../../assets/dashboard/menu.svg";
import UploadFileModal from "../common/UploadFileModal";

// Same icon set as the mobile admin drawer (AdminMobileTopbar.jsx), so
// both interfaces look consistent - inline SVG with stroke="currentColor"
// instead of image files, so hover color just follows normal text-color
// classes instead of the old CSS-filter-on-<img> workaround.
const ICONS = {
  newClient: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <line x1="19" y1="8" x2="19" y2="14" />
      <line x1="16" y1="11" x2="22" y2="11" />
    </svg>
  ),
  search: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  ),
  upload: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="12" y1="18" x2="12" y2="12" />
      <polyline points="9 15 12 12 15 15" />
    </svg>
  ),
  link: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  ),
  headset: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
      <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
    </svg>
  ),
  calendar: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <rect x="3" y="4" width="18" height="18" rx="3" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  trending: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
      <polyline points="17 6 23 6 23 12" />
    </svg>
  ),
  users: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
};

const AdminSidebar = ({
  logoIcon,
  logout,
  labelClassName,
  onUploadClick = null,
  onToggle = () => {},
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const role = sessionStorage.getItem("userRole");
  
  // Load pinned state from localStorage, default to false (unpinned)
  const [isPinned, setIsPinned] = useState(() => {
    const saved = localStorage.getItem("adminSidebarPinned");
    return saved === "true";
  });
  const [showUploadModal, setShowUploadModal] = useState(false);
  
  // Sidebar is expanded when pinned
  const isExpanded = isPinned;

  // Notify parent of initial state on mount
  useEffect(() => {
    onToggle(isPinned);
  }, []); // Only on mount

  const handleLogo = () => {
    if (role === "Admin") {
      navigate("/dashboard");
    } else if (role === "Agent") {
      navigate("/agent");
    }
  };

  const handleLogout = async () => {
    try {
      await directLogout();
      sessionStorage.clear();
      localStorage.clear();
      navigate("/login");
    } catch (err) {
      console.error("Logout failed:", err);
    }
  };

  const navigationItems = [
    {
      icon: ICONS.newClient,
      alt: "Add new client",
      onClick: () => navigate("/new-client"),
      label: t("dashboard.addClient"),
    },
    {
      icon: ICONS.search,
      alt: "Contact Search",
      onClick: () => navigate("/admin/contact-search"),
      label: t("dashboard.contactSearch"),
    },
    {
      icon: ICONS.upload,
      alt: "Upload Contact List",
      onClick: () => {
        if (typeof onUploadClick === "function") {
          onUploadClick();
        } else {
          setShowUploadModal(true);
        }
      },
      label: t("dashboard.uploadList"),
    },
    {
      icon: ICONS.link,
      alt: "Assign Client",
      onClick: () => navigate("/assign-client"),
      label: t("dashboard.assignClient"),
    },
    {
      icon: ICONS.headset,
      alt: "Add New User",
      onClick: () => navigate("/new-user"),
      label: t("dashboard.addUser"),
    },
    {
      icon: ICONS.calendar,
      alt: "Agent Dashboard",
      onClick: () => navigate("/client"),
      label: t("dashboard.scheduledMeetings"),
    },
    {
      icon: ICONS.trending,
      alt: "Agent Dashboard",
      onClick: () => navigate("/agent"),
      label: t("dashboard.agentDashboard"),
    },
    {
      icon: ICONS.users,
      alt: "Client Dashboard",
      onClick: () => navigate("/client-dashboard"),
      label: t("dashboard.dashboardClient"),
    },
  ];

  const toggleSidebar = () => {
    const newPinnedState = !isPinned;
    setIsPinned(newPinnedState);
    // Persist to localStorage
    localStorage.setItem("adminSidebarPinned", String(newPinnedState));
    // Notify parent component about the change
    onToggle(newPinnedState);
  };

  return (
    <div
      className={`fixed top-0 z-50 left-0 h-screen flex flex-col justify-between items-center py-6 px-4 transition-all duration-300 ${
        isExpanded ? "w-[250px]" : "w-[80px]"
      } ${labelClassName}`}
    >
      <div className="flex flex-col items-center gap-2 mb-8 w-full">
        <img
          src={logoIcon}
          alt="Logo"
          className="h-[53px] w-[48px] cursor-pointer"
          onClick={handleLogo}
        />
        <button
          onClick={toggleSidebar}
          className="flex items-center justify-center w-7 h-7 rounded-md text-white hover:bg-white/10 transition-colors"
          title={t("button.toggle")}
        >
          <img src={menu} alt={isPinned ? "unpin" : "pin"} className="w-4 h-4" />
        </button>
      </div>

        {/* <div data-tour-id="nav-Item"> */}
      <div className="flex flex-col gap-6 flex-1 w-full mt-5" data-tour-id="nav-Item">
          {navigationItems.map((item, index) => (
            <div
              key={index}
              className={`relative group flex items-center gap-3 cursor-pointer transition-opacity mb-4 ${
                isExpanded ? "justify-start ml-3" : "justify-center"
              }`}
              onClick={item.onClick}
            >
              <span
                className={`w-6 h-6 flex-shrink-0 text-white transition-colors group-hover:text-secondary [&>svg]:w-full [&>svg]:h-full`}
              >
                {item.icon}
              </span>

              {isExpanded && (
                <span className="text-white text-sm whitespace-nowrap group-hover:text-secondary transition-all ease-in-out">
                  {item.label}
                </span>
              )}

              {!isExpanded && (
                <span className="absolute left-full px-2 py-1 bg-white text-primary text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 whitespace-nowrap z-50 transition-opacity">
                  {item.label}
                </span>
              )}
            </div>
          ))}
      </div>
        {/* </div> */}

      {/* Logout */}
      <div
        className={`flex items-center gap-3 w-full mt-5 ${
          isExpanded ? "justify-start" : "justify-center"
        }`}
      >
        <img
          src={logout}
          alt="logout"
          className="w-[36px] cursor-pointer hover:opacity-80 transition-opacity"
          onClick={handleLogout}
        />
      </div>
      <UploadFileModal
        open={showUploadModal}
        onClose={() => setShowUploadModal(false)}
      />
    </div>
  );
};

export default AdminSidebar;
